import React, { useState } from "react";
import {
  IconCrown,
  IconRocket,
  IconCheck,
  IconInfinity,
  IconX,
  IconStar,
  IconArrowRight,
  IconSparkles,
  IconBuildingStore,
  IconArrowsExchange,
  IconHeadset,
  IconClock,
  IconAlertCircle,
} from "@tabler/icons-react";
import PlanServices from "../../../services/PlanServices";

// Recursos principais para mostrar nos cards
const MAIN_RESOURCES = [
  "consultoria",
  "subaccounts",
  "whatsapp_instances",
  "users_per_subaccount",
  "users",
  "agents",
  "playground",
  "whatsapp_messages",
  "contacts_base",
  "schedules",
  "products",
  "support",
];

// Planos ativos pessoais
const ACTIVE_PERSONAL_KEYS = ["starter", "personal_business"];
// Planos ativos comerciais (WhiteLabel)
const ACTIVE_COMMERCIAL_KEYS = ["white_label", "wl_plus", "wl_pro"];
// Planos legados (só mostram se forem o plano atual)
const LEGACY_PERSONAL_KEYS = ["gratuito"];
const LEGACY_COMMERCIAL_KEYS = ["ondemand", "business"];

const renderResourceValue = (resource) => {
  if (!resource) return null;

  if (resource.is_disabled) {
    return (
      <span className="d-flex align-items-center">
        <IconX size={16} className="text-muted" />
      </span>
    );
  }

  if (resource.is_boolean && resource.value) {
    return (
      <span className="d-flex align-items-center">
        <IconCheck size={16} className="text-success" />
      </span>
    );
  }

  if (resource.is_unlimited) {
    return (
      <span className="d-flex align-items-center gap-1">
        <IconInfinity size={16} className="text-primary" />
        <span className="fw-semibold">Ilimitado</span>
      </span>
    );
  }

  return (
    <span className="fw-bold">
      {resource.value}
      {resource.unit && <small className="text-muted ms-1">{resource.unit}</small>}
    </span>
  );
};

const PlanCard = ({
  plan,
  isCurrentPlan,
  isUpgrade,
  isRecommended,
  isLegacy,
  isTrial,
  isTrialExpired,
  daysRemaining,
  onSelectPlan,
  index,
}) => {
  // Extrair recursos com is_highlight para destaque
  const highlightResources = Object.entries(plan.resources || {}).filter(
    ([_, resource]) => resource?.is_highlight
  );

  const showTrialActiveBadge = isCurrentPlan && isTrial && !isTrialExpired;
  const showTrialExpiredBadge = isCurrentPlan && isTrialExpired;
  const showCurrentBadge = isCurrentPlan && !isTrial && !isTrialExpired;

  return (
    <div
      className={`plan-card fade-in ${isCurrentPlan ? 'current' : ''} ${isRecommended ? 'recommended' : ''} ${isLegacy ? 'legacy' : ''}`}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      {/* Badge de destaque */}
      {isRecommended && !isCurrentPlan && (
        <div className="plan-card-badge">
          <IconStar size={12} /> Recomendado
        </div>
      )}
      {showCurrentBadge && (
        <div className="plan-card-badge current">
          <IconCheck size={12} /> Plano Atual
        </div>
      )}
      {showTrialActiveBadge && (
        <div
          className="plan-card-badge"
          style={{ background: '#f59e0b', color: '#fff' }}
        >
          <IconClock size={12} />
          {typeof daysRemaining === 'number'
            ? ` Período de teste — ${daysRemaining} ${daysRemaining === 1 ? 'dia' : 'dias'} restantes`
            : ' Período de teste'}
        </div>
      )}
      {showTrialExpiredBadge && (
        <div
          className="plan-card-badge"
          style={{ background: '#dc2626', color: '#fff' }}
        >
          <IconAlertCircle size={12} /> Período de teste expirado
        </div>
      )}
      {isLegacy && !isCurrentPlan && (
        <div className="plan-card-badge legacy">
          Legado
        </div>
      )}

      {/* Header */}
      <div className="plan-card-header">
        <div className={`plan-card-icon ${isRecommended ? 'recommended' : ''}`}>
          <IconCrown size={32} />
        </div>
        <h3 className="plan-card-name">{plan.name}</h3>
        <p className="plan-card-description">{plan.description}</p>
      </div>

      {/* Pricing */}
      <div className="plan-card-pricing">
        {plan.monthly_value > 0 ? (
          <>
            <span className="plan-card-price">
              <span className="plan-card-price-currency">R$</span>
              {Math.floor(plan.monthly_value)}
            </span>
            <span className="plan-card-price-period">/mês</span>
          </>
        ) : (
          <span className="plan-card-price" style={{ color: 'var(--success, #06b776)' }}>
            Grátis
          </span>
        )}
      </div>

      {/* Highlight Resources (ex: consultoria do WL Pro) */}
      {highlightResources.length > 0 && (
        <div className="plan-card-highlight">
          {highlightResources.map(([key, resource]) => (
            <div key={key} className="plan-card-highlight-item">
              <IconHeadset size={18} />
              <span>
                {resource.label}
                {resource.value && !resource.is_boolean && (
                  <strong>: {resource.value}</strong>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Features */}
      <div className="plan-card-features">
        <ul className="plan-card-features-list">
          {MAIN_RESOURCES.map((resourceKey) => {
            const resource = plan.resources?.[resourceKey];
            if (!resource) return null;
            // Não duplicar itens de highlight (ja renderizados acima)
            if (resource.is_highlight) return null;

            const hasValue = resource.is_disabled ? false : (resource.value || resource.is_unlimited || resource.is_boolean);

            return (
              <li
                key={resourceKey}
                className={`plan-card-feature-item ${!hasValue ? 'disabled' : ''}`}
              >
                <span className="plan-card-feature-icon">
                  {hasValue ? <IconCheck size={12} /> : <IconX size={12} />}
                </span>
                <span className="flex-grow-1">{resource.label}</span>
                {hasValue && (
                  <span className="text-end">
                    {renderResourceValue(resource)}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {/* Footer / CTA */}
      <div className="plan-card-footer">
        {isCurrentPlan && (isTrial || isTrialExpired) ? (
          <button
            className="plan-card-button primary"
            onClick={() => onSelectPlan(plan, { mode: 'subscribe' })}
          >
            <IconRocket size={18} />
            Contratar Agora
            <IconArrowRight size={16} />
          </button>
        ) : isCurrentPlan ? (
          <button className="plan-card-button current" disabled>
            <IconCheck size={18} />
            Seu Plano Atual
          </button>
        ) : isUpgrade ? (
          <button
            className="plan-card-button primary"
            onClick={() => onSelectPlan(plan)}
          >
            <IconRocket size={18} />
            Fazer Upgrade
            <IconArrowRight size={16} />
          </button>
        ) : (
          <button className="plan-card-button outline" disabled>
            Indisponível
          </button>
        )}
      </div>
    </div>
  );
};

const AvailablePlansGrid = ({ plans, currentPlan, onSelectPlan }) => {
  const [showCommercialPlans, setShowCommercialPlans] = useState(false);

  const currentPlanId = currentPlan?.id;
  const isPersonal = currentPlan?.is_personal || currentPlan?.usage_type === 1;

  const normalizeId = (id) => String(id);
  const normalizedCurrentId = normalizeId(currentPlanId);

  // Resolver planos por chave, filtrando nulos
  const resolvePlans = (keys) =>
    keys.map((key) => PlanServices.getPlanById(key)).filter(Boolean);

  // Planos ativos sempre visíveis
  const activePersonalPlans = resolvePlans(ACTIVE_PERSONAL_KEYS);
  const activeCommercialPlans = resolvePlans(ACTIVE_COMMERCIAL_KEYS);

  // Planos legados: só entram na lista se forem o plano atual
  const legacyPersonalPlans = resolvePlans(LEGACY_PERSONAL_KEYS).filter(
    (plan) => normalizeId(plan.id) === normalizedCurrentId
  );
  const legacyCommercialPlans = resolvePlans(LEGACY_COMMERCIAL_KEYS).filter(
    (plan) => normalizeId(plan.id) === normalizedCurrentId
  );

  // Lista final por categoria (legado primeiro para destacar o plano atual)
  const personalPlans = [...legacyPersonalPlans, ...activePersonalPlans];
  const commercialPlans = [...legacyCommercialPlans, ...activeCommercialPlans];

  // IDs legados para marcar badge "Legado"
  const legacyIds = new Set(
    [...LEGACY_PERSONAL_KEYS, ...LEGACY_COMMERCIAL_KEYS]
      .map((key) => normalizeId(PlanServices.getPlanById(key)?.id))
      .filter(Boolean)
  );

  const renderPlanCard = (plan, index) => {
    const planIdNormalized = normalizeId(plan.id);
    const isCurrentPlan = planIdNormalized === normalizedCurrentId;
    const isUpgrade = currentPlanId
      ? PlanServices.isPlanUpgrade(normalizedCurrentId, planIdNormalized)
      : true;
    const isRecommended = plan.highlight && !isCurrentPlan;
    const isLegacy = legacyIds.has(planIdNormalized);
    const isTrial = isCurrentPlan && !!currentPlan?.is_trial && !currentPlan?.trial_expired;
    const isTrialExpired = isCurrentPlan && !!currentPlan?.trial_expired;

    return (
      <PlanCard
        key={plan.id}
        plan={plan}
        isCurrentPlan={isCurrentPlan}
        isUpgrade={isUpgrade}
        isRecommended={isRecommended}
        isLegacy={isLegacy}
        isTrial={isTrial}
        isTrialExpired={isTrialExpired}
        daysRemaining={currentPlan?.days_remaining}
        onSelectPlan={onSelectPlan}
        index={index}
      />
    );
  };

  return (
    <div>
      {/* Seção: Planos Pessoais (para quem está no uso pessoal) */}
      {isPersonal && (
        <>
          <div className="myplan-section-header">
            <h2 className="myplan-section-title">
              <IconSparkles size={24} />
              Planos para Uso Pessoal
            </h2>
            {currentPlan && (
              <span className="plan-status-badge active" style={{ fontSize: '12px', padding: '6px 12px' }}>
                Plano atual: {currentPlan.name}
              </span>
            )}
          </div>

          <div className="plans-grid">
            {personalPlans.map((plan, i) => renderPlanCard(plan, i))}
          </div>

          {/* Separador: Alterar para comercial */}
          <div
            className="myplan-switch-section"
            style={{
              textAlign: 'center',
              margin: '40px 0 24px',
              padding: '24px',
              background: 'var(--surface, #f8fafc)',
              borderRadius: 'var(--border-radius-lg, 16px)',
              border: '1px dashed var(--border-color, #e2e8f0)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
              <IconBuildingStore size={20} style={{ color: 'var(--primary, #3b82f6)' }} />
              <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: 'var(--text-primary, #0f172a)' }}>
                Precisa de mais recursos?
              </h3>
            </div>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary, #475569)', marginBottom: '16px' }}>
              Altere sua conta pessoal para comercial e tenha acesso a White Label, subcontas e muito mais.
            </p>
            <button
              className="plan-card-button primary"
              style={{ maxWidth: '320px', margin: '0 auto' }}
              onClick={() => setShowCommercialPlans(!showCommercialPlans)}
            >
              <IconArrowsExchange size={18} />
              {showCommercialPlans ? 'Ocultar planos comerciais' : 'Ver planos comerciais'}
              <IconArrowRight size={16} />
            </button>
          </div>

          {/* Planos comerciais (expandível — apenas ativos, sem legados) */}
          {showCommercialPlans && (
            <>
              <div className="myplan-section-header" style={{ marginTop: '24px' }}>
                <h2 className="myplan-section-title">
                  <IconBuildingStore size={24} />
                  Planos Comerciais
                </h2>
              </div>
              <div className="plans-grid">
                {activeCommercialPlans.map((plan, i) => renderPlanCard(plan, i))}
              </div>
            </>
          )}
        </>
      )}

      {/* Seção: Planos Comerciais (para quem já está no uso comercial) */}
      {!isPersonal && (
        <>
          <div className="myplan-section-header">
            <h2 className="myplan-section-title">
              <IconSparkles size={24} />
              Planos Disponíveis
            </h2>
            {currentPlan && (
              <span className="plan-status-badge active" style={{ fontSize: '12px', padding: '6px 12px' }}>
                Plano atual: {currentPlan.name}
              </span>
            )}
          </div>

          <div className="plans-grid">
            {commercialPlans.map((plan, i) => renderPlanCard(plan, i))}
          </div>
        </>
      )}
    </div>
  );
};

export default AvailablePlansGrid;
